'use client'

import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { DefinitionList } from '@/ui/lists/definition-list/DefinitionList.client'
import { gql } from '@gql-generated/gql'
import type {
  ProductTechnicalSpecsFull_NormsFragment,
  ProductTechnicalSpecs_TechnicalSpecsFragment,
} from '@gql-generated/graphql'

gql(/* GraphQL */ `
  fragment ProductTechnicalSpecs_TechnicalSpecs on TechnicalSpec {
    label
    value
  }

  fragment ProductTechnicalSpecsFull_Norms on Norm {
    id
    name
  }
`)

type NormalizedSpec = {
  label: string
  values: string[]
}

type ProductTechnicalSpecsProps = {
  /** The technical specs to show */
  technicalSpecs: ProductTechnicalSpecs_TechnicalSpecsFragment[]

  /** Norms to append at the end of specs */
  norms?: ProductTechnicalSpecsFull_NormsFragment[] | null

  /** Additional classes to add to the component */
  className?: string
}

/** Normalizes technical specs and norms into a unified format */
function normalizeSpecs(
  technicalSpecs: ProductTechnicalSpecs_TechnicalSpecsFragment[],
  normsTitle: string,
  norms?: ProductTechnicalSpecsFull_NormsFragment[] | null,
): NormalizedSpec[] {
  // Normalize technical specs: keep value as single element array
  const normalizedTechnicalSpecs: NormalizedSpec[] = technicalSpecs
    .filter((spec) => spec.label && spec.value)
    .map((spec) => ({
      label: spec.label ?? '',
      values: [spec.value ?? ''],
    }))

  // Normalize norms: combine all norms into a single row
  if (norms?.length) {
    const normalizedNorms: NormalizedSpec = {
      label: normsTitle,
      values: norms.map((norm) => norm.name),
    }

    return [...normalizedTechnicalSpecs, normalizedNorms]
  }

  return normalizedTechnicalSpecs
}

export function ProductTechnicalSpecs({
  technicalSpecs,
  norms,
  className,
}: ProductTechnicalSpecsProps) {
  const { t } = useTranslation(['productPage'])
  const normsTitle = t('productPage:norms.title')

  if (!technicalSpecs?.length) {
    return null
  }

  const normalizedSpecs = normalizeSpecs(technicalSpecs, normsTitle, norms)

  return (
    <div className={className} data-testid="product-technical-specs">
      <DefinitionList aria-label={t('productPage:technicalSpecs.title')}>
        <DefinitionList.Column>
          {normalizedSpecs.map(({ label }) => (
            <DefinitionList.Item key={label}>{label}</DefinitionList.Item>
          ))}
        </DefinitionList.Column>
        <DefinitionList.Column>
          {normalizedSpecs.map(({ label, values }) => (
            <DefinitionList.Item key={label} className="whitespace-pre-line">
              {values.join('\n')}
            </DefinitionList.Item>
          ))}
        </DefinitionList.Column>
      </DefinitionList>
    </div>
  )
}
